// TODO: Implement commission dialog to display fees per merchant terminal.
//       - Fetch commission data using the merchant terminal ID.
//       - Add a selector dropdown for switching between terminals.
//       - Split commission values into two columns: net cost and gross cost.
//
// Additional: The commissions should be displayed per terminal, and users must be
// able to switch between different terminals easily.

import React, { useState, useEffect } from 'react';
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Dialog,
    Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterService from '../../../services/filterService/FilterService';
import { formatBackendError } from '../../../utils/formatError';
import i18n from '../../../i18n';

const CommissionAccordion = ({ commission }) => (
    <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{commission.name}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            {commission.fee && (
                <Typography>
                    <strong>{i18n.t('columns.fee')}:</strong> {commission.fee}
                </Typography>
            )}
            {commission.minimalCommission && (
                <Typography>
                    <strong>{i18n.t('columns.minimalCommission')}:</strong> {commission.minimalCommission}
                </Typography>
            )}
            {commission.dimensions && (
                <Typography>
                    <strong>{i18n.t('columns.dimension')}:</strong>
                    {i18n.t(`enums.${commission.dimensions}`)}
                </Typography>
            )}
            {commission.calculationType && (
                <Typography>
                    <strong>{i18n.t('columns.calculationType')}:</strong>{' '}
                    {i18n.t(`enums.${commission.calculationType}`)}
                </Typography>
            )}
            {commission.commissionType && (
                <Typography>
                    <strong>{i18n.t('columns.commissionType')}:</strong>{' '}
                    {commission.commissionType?.name}{' ('}
                    {commission.commissionType?.description}{')'}
                </Typography>
            )}
            {commission.comment && (
                <Typography>
                    <strong>{i18n.t('columns.comment')}:</strong>{' '}
                    {commission.comment}
                </Typography>
            )}
        </AccordionDetails>
    </Accordion>
);

const CommissionDialog = ({
    open,
    handleClose,
    terminalsServer,
    commissionsServer,
    selectedTerminalId
}) => {
    const [terminals, setTerminals] = useState([]);
    const [selectedTerminal, setSelectedTerminal] =
        useState(selectedTerminalId);
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        terminalsServer.getAll().then((res) => {
            if (res.error === 'OK') {
                setTerminals(res.data);
            } else {
                setError(formatBackendError(res));
            }
        });
    }, []);
    useEffect(() => {
        if (selectedTerminal) {
            setLoading(true);
            const filterBySelectedTerminal = new FilterService()
                .addFilter(
                    'terminal.id',
                    FilterService.OPERATORS.EQUAL,
                    selectedTerminal
                )
                .getFilters();

            commissionsServer
                .getAllFiltered(filterBySelectedTerminal, [], {})
                .then((res) => {
                    if (res.error === 'OK') {
                        setCommissions(res.data);
                        setLoading(false);
                    } else {
                        setError(formatBackendError(res));
                        setLoading(false);
                    }
                });
        }
    }, [selectedTerminal]);

    // Grouping commissions into Net Cost and Gross Cost
    const netCostCommissions = commissions.filter((c) => c.isNetCost);
    const grossCostCommissions = commissions.filter((c) => !c.isNetCost);

    return (
        <Dialog open={open} maxWidth={'md'} fullWidth onClose={handleClose}>
            <div className="p-6">
                {error && <Alert severity="error">{error}</Alert>}
                <FormControl fullWidth variant="outlined">
                    <InputLabel id="terminal-select-label">
                        {i18n.t('columns.merchantTerminal')}
                    </InputLabel>
                    <Select
                        labelId="terminal-select-label"
                        value={selectedTerminal}
                        onChange={(e) => setSelectedTerminal(e.target.value)}
                        label={i18n.t('columns.merchantTerminal')}
                        renderValue={(selected) =>
                            selected
                                ? terminals.find((t) => t.id === selected)?.name
                                : i18n.t('columns.merchantTerminal')
                        }
                        MenuProps={{
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            },
                            getContentAnchorEl: null,
                            PaperProps: {
                                style: {
                                    maxHeight: 250,
                                    overflowY: 'auto',
                                    width: 'auto'
                                }
                            }
                        }}
                    >
                        {terminals.map((terminal) => (
                            <MenuItem key={terminal.id} value={terminal.id}>
                                {terminal.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {loading ? (
                    <div className="flex justify-center items-center mt-auto h-32">
                        <CircularProgress />
                    </div>
                ) : (
                    <TableContainer component={Paper} className="mt-4">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="font-bold">
                                        {i18n.t('common.netCost')}
                                    </TableCell>
                                    <TableCell className="font-bold">
                                        {i18n.t('common.grossCost')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {netCostCommissions.length === 0 &&
                                    grossCostCommissions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {i18n.t(
                                                    'common.noCommissions'
                                                )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {Array.from({
                                    length: Math.max(
                                        netCostCommissions.length,
                                        grossCostCommissions.length
                                    )
                                }).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {netCostCommissions[index] ? (
                                                <CommissionAccordion
                                                    commission={
                                                        netCostCommissions[
                                                            index
                                                        ]
                                                    }
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {grossCostCommissions[index] ? (
                                                <CommissionAccordion
                                                    commission={
                                                        grossCostCommissions[
                                                            index
                                                        ]
                                                    }
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </Dialog>
    );
};

export default CommissionDialog;
